import type { PageSetup } from '../model';
import type { Annotation, DrawingDocument, ReactionArrowAnnotation } from '../model';
import type { ChemMoleculeV1Document } from '../domain/moleculeProjection.ts';
import type { ChemIsomerV1Document } from '../domain/isomerDocument.ts';
import type { PaperSize } from '../domain/geometry.ts';
import { generateIsomerDepiction } from './isomerDepiction.ts';

export type ParticipantRole = 'reactant' | 'product' | 'reagent' | 'catalyst' | 'solvent';

export const PARTICIPANT_ROLES: readonly ParticipantRole[] = ['reactant', 'product', 'reagent', 'catalyst', 'solvent'];

// A picked-but-not-yet-saved participant, as accumulated by the isomer picker harness in
// App.tsx. Distinct from ReactionParticipant (below), which additionally carries the generated
// depiction and is only assembled at save time.
export interface ReactionParticipantPick {
  readonly isomerId: string;
  readonly moleculeId: string;
  readonly isomerRevision: number;
  readonly role: ParticipantRole;
}

export interface ParticipantCoordinateSet {
  readonly id: string;
  readonly kind: 'depiction-2d';
  readonly isomerId: string;
  readonly isomerRevision: number;
  readonly dimensions: 2;
  readonly units: 'drawing-unit';
  readonly width: number;
  readonly height: number;
  readonly coordinates: DrawingDocument['depiction']['coordinateSet']['coordinates'];
  readonly depths?: Readonly<Record<string, number>>;
}

export interface ParticipantDepiction {
  readonly id: string;
  readonly revision: number;
  readonly isomerId: string;
  readonly isomerRevision: number;
  readonly coordinateSet: ParticipantCoordinateSet;
  readonly atomLabels: DrawingDocument['depiction']['atomLabels'];
  readonly bondStyles: DrawingDocument['depiction']['bondStyles'];
  readonly bondLengthOverrides: DrawingDocument['depiction']['bondLengthOverrides'];
  readonly atomAppearance?: DrawingDocument['depiction']['atomAppearance'];
  /** #736 step 2: groups drawn as their labels -- a depiction choice, kept with it. */
  readonly contractedGroups?: DrawingDocument['depiction']['contractedGroups'];
  readonly cycleFills?: DrawingDocument['depiction']['cycleFills'];
  readonly bondAppearance?: DrawingDocument['depiction']['bondAppearance'];
  // ADR 0022 D4: hydrogen bonds drawn on the paper -- no bonds of the molecule, so kept here.
  readonly hydrogenBonds?: Readonly<Record<string, { readonly source: string; readonly target: string }>>;
  // ADR 0026 D6: agostic interactions, likewise -- a C–H bond's hydrogen to a metal.
  readonly agosticBonds?: Readonly<Record<string, { readonly hydrogenAtomId: string; readonly metalAtomId: string }>>;
  // #951: hydrogens drawn as atoms that the Molecule holds as implicit -- not in it, so kept here.
  readonly drawnHydrogens?: Readonly<Record<string, { readonly atomId: string; readonly bondId: string }>>;
}

// --- Reaction arrow (ADR 0008) ---------------------------------------------------------------
// `reactionDrawing.arrow` lives only in drawing-v11.schema.json, and the v11 type surface isn't
// code-generated, so this stays hand-authored. The `curvedArrowAnnotation` / `shapeAnnotation` /
// `annotationAnchor` kinds are in *both* schemas as of 0.1.5 and come from `../model` (generated).

export type ReactionArrowKind =
  'forward' | 'equilibrium' | 'resonance' | 'retrosynthetic' | 'rearrangement' | 'dashed' | 'wavy' | 'dipole';

export const REACTION_ARROW_KINDS: readonly ReactionArrowKind[] = [
  'forward',
  'equilibrium',
  'resonance',
  'retrosynthetic',
  'rearrangement',
  'dashed',
  'wavy',
  'dipole'
];

export interface ReactionArrow {
  readonly kind: ReactionArrowKind;
  // A "reaction cannot happen" mark across the shaft; only meaningful with kind 'forward'.
  readonly overlay?: 'x' | 'slash';
}

export const DEFAULT_REACTION_ARROW: ReactionArrow = { kind: 'forward' };

// --- Reaction-arrow *annotation* (peruselab-platform #395) -----------------------------------
// A scheme arrow dropped on the plain /draw canvas as decoration -- no reaction-graph meaning
// (that is the single `ReactionArrow` above, owned by the composer). `reactionArrowAnnotation`
// is a member of the generated `Annotation` union in *both* v10 and v11 schemas (0.1.10), so
// these are re-derived from the generated type rather than re-declared.

export type ReactionArrowAnnotationKind = ReactionArrowAnnotation['arrowKind'];
export type ReactionArrowOverlay = NonNullable<ReactionArrowAnnotation['overlay']>;

// Palette order for the left-rail "Reaction arrows" group: the eight scheme arrows first
// (matching REACTION_ARROW_KINDS), then the two vertical forms.
export const REACTION_ARROW_ANNOTATION_KINDS: readonly ReactionArrowAnnotationKind[] = [
  'forward',
  'equilibrium',
  'equilibrium-unequal',
  'resonance',
  'retrosynthetic',
  'rearrangement',
  'dashed',
  'wavy',
  'dipole',
  'vertical-up',
  'vertical-equilibrium'
];

export const REACTION_ARROW_OVERLAYS: readonly ReactionArrowOverlay[] = ['x', 'slash', 'single-bar'];

// Everything a participant's `annotations` array holds -- the generated `Annotation` union
// already covers note / tag / arrow / angle plus the ADR 0008 curved-arrow / shape kinds.
export type ParticipantAnnotation = Annotation;

export interface ReactionParticipant {
  readonly isomerId: string;
  readonly isomerRevision: number;
  readonly role: ParticipantRole;
  readonly depiction: ParticipantDepiction;
  readonly annotations: readonly ParticipantAnnotation[];
  /** #1137 -- shown on the paper as this text annotation (in the canvas), e.g. H2SO4 typed by the
   *  arrow, rather than as its drawn structure. Absent: shown as its structure. */
  readonly shownAs?: { readonly kind: 'text'; readonly annotationId: string };
}

export interface ReactionConditions {
  readonly temperature?: { readonly value: number; readonly unit: 'celsius' };
  readonly pressure?: { readonly value: number; readonly unit: 'atm' };
  readonly duration?: { readonly value: number; readonly unit: 'second' };
  readonly yieldPercent?: number;
  readonly notes?: string;
}

export interface ReactionDrawingDocument {
  readonly schemaVersion: 'chem-drawing.v11';
  readonly kind: 'reaction';
  readonly participants: readonly ReactionParticipant[];
  readonly conditions?: ReactionConditions;
  // Optional on read for back-compat (absent ⇒ a plain forward arrow); the composer always
  // writes it. One arrow per reaction -- multi-step schemes are a deferred topic (ADR 0008).
  readonly arrow?: ReactionArrow;
  // #772 -- the drawing's page (absent before #772: the default Letter applies).
  readonly page?: PageSetup;
  // #1140 (ADR 0025 D3) -- the paper it was drawn on: its size and its own objects (the arrow,
  // text, shapes), each participant's depiction holding where it sat. Absent before #1140.
  readonly canvas?: {
    readonly width: number;
    readonly height: number;
    readonly annotations: readonly Annotation[];
  };
}

// #129: contracts/drawing-v11.schema.json's participant.depiction/coordinateSet defs key
// themselves by isomerId/isomerRevision, where generateIsomerDepiction's v10-shaped
// DrawingDocument.depiction (#128) uses structureId/structureRevision -- everything else
// (coordinates, atomLabels, bondStyles, bondLengthOverrides) is identical. This is just that
// rename, not a new coordinate-generation path.
//
// #140: extracted out of buildParticipant so IllustrationElement's isomer variant (which needs
// the identical depiction shape, just not wrapped in a ReactionParticipant) can share it.
//
// #692 -- `isomerId` is the stored Isomer record's id (a UUID), passed explicitly: the isomer
// *document's* own `id` is copied from the drawing structure it came from (`graph-…`), so it is
// neither a UUID nor the record the server resolves -- saving with it was rejected.
export async function buildIsomerDepictionPayload(
  molecule: ChemMoleculeV1Document,
  isomer: ChemIsomerV1Document,
  isomerId: string,
  isomerRevision: number,
  paperSize: PaperSize
): Promise<ParticipantDepiction> {
  const document = await generateIsomerDepiction(molecule, isomer, paperSize);
  const { coordinateSet } = document.depiction;
  return {
    id: document.depiction.id,
    revision: document.depiction.revision,
    isomerId,
    isomerRevision,
    coordinateSet: {
      id: coordinateSet.id,
      kind: coordinateSet.kind,
      isomerId,
      isomerRevision,
      dimensions: coordinateSet.dimensions,
      units: coordinateSet.units,
      width: coordinateSet.width,
      height: coordinateSet.height,
      coordinates: coordinateSet.coordinates
    },
    atomLabels: document.depiction.atomLabels,
    bondStyles: document.depiction.bondStyles,
    bondLengthOverrides: document.depiction.bondLengthOverrides,
    // #868 -- per-atom / per-bond appearance travels with the depiction both ways.
    ...(document.depiction.cycleFills ? { cycleFills: document.depiction.cycleFills } : {}),
    ...(document.depiction.atomAppearance ? { atomAppearance: document.depiction.atomAppearance } : {}),
    ...(document.depiction.bondAppearance ? { bondAppearance: document.depiction.bondAppearance } : {}),
    ...(document.depiction.contractedGroups ? { contractedGroups: document.depiction.contractedGroups } : {})
  };
}

/** #692 -- `isomerId` is the Isomer record's id (see buildIsomerDepictionPayload). */
export async function buildParticipant(
  molecule: ChemMoleculeV1Document,
  isomer: ChemIsomerV1Document,
  isomerId: string,
  isomerRevision: number,
  role: ParticipantRole,
  paperSize: PaperSize
): Promise<ReactionParticipant> {
  return {
    isomerId,
    isomerRevision,
    role,
    depiction: await buildIsomerDepictionPayload(molecule, isomer, isomerId, isomerRevision, paperSize),
    annotations: []
  };
}
